/**
 * Browser Mode Demo Banner
 *
 * Shows a persistent banner at the top of the app in browser mode with:
 * - "SQLite-only browser mode" messaging
 * - Demo database info (when connected)
 * - Reset button (for demo databases)
 * - Export button (when connected)
 * - Persistence status
 * - Download CTA
 */

import { useState } from 'react';
import { Download, RotateCcw, Database, AlertTriangle, Save, ExternalLink, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useBrowserModeStore } from '../store/browserModeStore';
import { resetDemoDatabase, openDatabaseFromBuffer, exportCurrentDatabase, downloadDatabase } from '@/web/sqlite';
import { isBrowserMode, isEmbedMode, PRODUCT_NAME } from '../browserModeUtils';
import { isUploadedDatabase } from '../types/browserModeTypes';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
export function BrowserModeDemoBanner() {
  const {
    activeConnection,
    persistenceAvailable,
    isResetting,
    setIsResetting,
    setConnected,
  } = useBrowserModeStore();
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Only show in browser mode (and not in embed mode - homepage provides context)
  if (!isBrowserMode() || isEmbedMode()) {
    return null;
  }

  const isConnected = activeConnection && activeConnection.status === 'connected';
  const database = activeConnection?.database;
  const isUploaded = database && isUploadedDatabase(database);

  const handleResetClick = () => {
    setShowResetConfirm(true);
  };

  const handleResetConfirm = async () => {
    setShowResetConfirm(false);
    if (!database || isUploaded) return;

    setIsResetting(true);

    try {
      // Reset the database to baseline
      const buffer = await resetDemoDatabase(database.id, database.assetPath);

      // Reopen the database from the baseline bytes
      await openDatabaseFromBuffer(database.id, buffer);

      // Update store state
      setConnected(database);

      console.log('[BrowserModeDemoBanner] Database reset to baseline:', database.id);
    } catch (error) {
      console.error('[BrowserModeDemoBanner] Reset failed:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleExport = () => {
    if (!database) return;

    const data = exportCurrentDatabase();
    if (!data) {
      console.error('[BrowserModeDemoBanner] No database to export');
      return;
    }

    // Generate filename
    const baseName = isUploaded ? database.originalFilename : database.name;
    const filename = baseName.endsWith('.sqlite') ? baseName : `${baseName}.sqlite`;

    downloadDatabase(data, filename);
    console.log('[BrowserModeDemoBanner] Exported database:', filename);
  };

  return (
    <>
      <div
        className="flex items-center justify-between gap-4 px-3 py-1.5 bg-muted border-b border-border text-xs"
        data-testid="browser-mode-demo-banner"
      >
        {/* Left: Demo mode info + Database info */}
        <div className="flex items-center gap-3">
          {/* SQLite-only messaging with tooltip explaining limitations */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground flex items-center gap-1 cursor-help">
                SQLite-only browser mode
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="font-medium mb-1">Browser Mode Limitations</p>
              <ul className="text-xs space-y-0.5">
                <li>✓ SQLite — Full support</li>
                <li>✗ SQL Server — Desktop app required</li>
                <li>✗ PostgreSQL — Desktop app required</li>
                <li>✗ Terminal/CLI — Desktop app required</li>
              </ul>
            </TooltipContent>
          </Tooltip>

          {/* Database info when connected */}
          {isConnected && database && (
            <>
              <div className="h-3 w-px bg-border" />
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Database className="h-3.5 w-3.5" />
                <span>{isUploaded ? 'Uploaded:' : 'Demo:'}</span>
                <span className="font-medium text-foreground">{database.name}</span>
              </div>
            </>
          )}

          {/* Persistence warning */}
          {!persistenceAvailable && (
            <div className="flex items-center gap-1 text-warning">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Changes won't persist (private browsing)</span>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Export button (only when connected) */}
          {isConnected && database && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleExport}
              data-testid="export-database"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              Export
            </Button>
          )}

          {/* Reset button (only for demo databases when connected) */}
          {isConnected && database && !isUploaded && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleResetClick}
              disabled={isResetting}
              data-testid="reset-demo-database"
            >
              {isResetting ? (
                <RandomDotmSpinner size={14} className="mr-1" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
              )}
              Reset
            </Button>
          )}

          {/* Divider (when there are database actions) */}
          {isConnected && database && <div className="h-4 w-px bg-border" />}

          {/* Learn more link */}
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
            <a href="/?utm_source=app&utm_medium=referral&utm_campaign=demo-banner" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1" />
              Learn more
            </a>
          </Button>

          {/* Download CTA - primary action */}
          <Button size="sm" className="h-6 px-2 text-xs" asChild data-testid="download-cta-button">
            <a href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=demo-banner#download" target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5 mr-1" />
              Download {PRODUCT_NAME}
            </a>
          </Button>
        </div>
      </div>

      {/* Reset confirmation dialog */}
      {database && !isUploaded && (
        <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Demo Database?</AlertDialogTitle>
              <AlertDialogDescription>
                This will restore <strong>{database.name}</strong> to its original state.
                All changes you've made (INSERTs, UPDATEs, DELETEs) will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleResetConfirm}>Reset Database</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
